// List of currently selected refinements across all filters, shown at the top
// of the search results. It becomes sticky when scrolling down far enough
// and it expands/collapses dynamically based on the number of selected filters
// and what the user is doing

import React, { useState, useRef, useEffect } from "react";
import { connectCurrentRefinements } from "react-instantsearch-dom";

import string from "helpers/string";

// TODO interim: consolidate this with a master list of categories
const MAPPING = {
  "Lit Guide > Character": "Lit Guides: Characters",
  "Lit Guide > Lit Device": "Lit Guides: Literary Devices",
  "Lit Guide > Lit Guide": "Lit Guides: Titles",
  "Lit Guide > Other": "Lit Guides: Other",
  "Lit Guide > Quiz": "Lit Guides: Quizzes",
  "Lit Guide > Quotes": "Lit Guides: Quotes",
  "Lit Guide > Summary & Analysis": "Lit Guides: Summary & Analysis",
  "Lit Guide > Symbol": "Lit Guides: Symbols",
  "Lit Guide > Term": "Lit Guides: Terms",
  "Lit Guide > Theme": "Lit Guides: Themes",
  "Lit Term > Lit Term": "All Lit Terms",
  "Lit Terms > Other": "Lit Terms: Other",
  "Mythology > <n/a>": "Mythology: Other",
  "Play or Drama > <n/a>": "Play or Drama: Other",
  "Play or Drama > Musical": "Musicals",
  "Play or Drama > One-Act Play": "One-Act Plays",
  "Play or Drama > Verse Drama": "Verse Dramas",
  "Poetry > <n/a>": "Poetry: Other",
  "Poetry > Lyric Poetry": "Lyric Poetry",
  "Poetry > Narrative Poetry": "Narrative Poetry",
  "Poetry Guide > Other": "Poetry Guides: Other",
  "Poetry Guide > Poetic Device": "Poetry Guides: Poetic Devices",
  "Poetry Guide > Poetry Guide": "Poetry Guides: Titles",
  "Poetry Guide > Symbol": "Poetry Guides: Symbols",
  "Poetry Guide > Theme": "Poetry Guides: Themes",
  "Prose Fiction > <n/a>": "Other",
  "Prose Fiction > Folktale, Fable, or Fairy Tale":
    "Folktales, Fables, or Fairy Tales",
  "Prose Fiction > Graphic Novel": "Graphic Novels",
  "Prose Fiction > Novel": "Novels",
  "Prose Fiction > Novella": "Novellas",
  "Prose Fiction > Short Story / Story Collection":
    "Short Stories/Story Collections",
  "Prose Nonfiction > <n/a>": "Prose Nonfiction: Other",
  "Prose Nonfiction > Autobiography / Memoir": "Autobiographies/Memoirs",
  "Prose Nonfiction > Biography": "Biographies",
  "Prose Nonfiction > Diary or Journal": "Diaries or Journals",
  "Prose Nonfiction > Document": "Documents",
  "Prose Nonfiction > Essay": "Essays",
  "Prose Nonfiction > Graphic Memoir / Biography":
    "Graphic Memoirs/Biographies",
  "Prose Nonfiction > History": "History",
  "Prose Nonfiction > Philosophy & Political Philosophy":
    "Philosophy & Political Philosophy",
  "Prose Nonfiction > Sermon, Speech, or Lecture":
    "Sermons, Speeches, or Lectures",
  "Prose Nonfiction > Sociology & Social Science": "Sociology & Social Science",
  "Prose Nonfiction > Theology": "Theology",
  "Shakescleare > Other": "Shakescleare: Other",
  "Shakescleare > Scene": "Shakescleare: Scenes",
  "Shakescleare > Shakescleare": "Shakescleare: Titles"
};

// Each individual selected refinement
const CurrentRefinementPill = (props) => {
  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div
      className="current-refinements__refinement"
      onClick={() => props.refine(props.value)}
    >
      {props.text}
      <i className="lc-icon lc-icon-xs lc-icon-close" />
    </div>
  );
};

const getLabel = (label, item) => {
  if (item.attribute === "literaryPeriod") {
    return `${label.split(" (")[0]}...`;
  }
  if (item.attribute === "gradeLevel") {
    if (label === "Higher education") return label;
    return string.cardinalize(label);
  }
  if (["category", "type"].indexOf(item.attribute) > -1) {
    return string.truncate(MAPPING[label] || "UNLABELED", 31);
  }

  return string.truncate(label, 31);
};

const BOOTSTRAP_COL_PADDING = 30;

// The current refinements container becomes fixed when scrolling down so it needs
// a fixed pixel width
const handleResize = () => {
  $(".current-refinements").width(
    $(".current-refinements").parent().outerWidth() - BOOTSTRAP_COL_PADDING
  );
};

const CurrentRefinements = (props) => {
  // If the current refinement is fixed to the top of the page
  const [isSticky, setIsSticky] = useState(false);

  // If current refinement is expanded to show all refinements or collapsed
  const [isExpanded, setIsExpanded] = useState(true);

  // Number of hidden refinements
  const [hiddenRefinements, setHiddenRefinements] = useState(0);

  const containerRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => {
    window.addEventListener("resize", handleResize);

    handleResize();

    return function cleanup() {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll, { passive: true });

    return function cleanup() {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [isSticky]);

  // Always expand list when an item is added/removed
  useEffect(() => {
    setIsExpanded(true);
  }, [props.items]);

  // When shrinking on scroll, set the hidden refinement count for the more button
  useEffect(() => {
    if (!isExpanded) {
      let count = 0;

      let top = -1;
      // eslint-disable-next-line no-restricted-syntax
      for (const child of listRef.current.children) {
        if (top < 0) {
          top = child.getBoundingClientRect().top;
        } else if (child.getBoundingClientRect().top > top) {
          count += 1;
        }
      }

      setHiddenRefinements(count);
    }
  }, [isExpanded]);

  // On scroll, handle sticky status of the refinement container
  const handleScroll = () => {
    if (containerRef.current) {
      if (isSticky) {
        setIsSticky($(".search__searchbar")[0].getBoundingClientRect().top < 0);
      } else {
        setIsSticky(containerRef.current.getBoundingClientRect().top <= 0);
      }
    }

    setIsExpanded(false);
  };

  const expandList = () => {
    setIsExpanded(true);
  };

  const itemsToRender = [];

  // The items array may have different children depending on the type of filter
  // and which filter is selected, this method covers each case
  props.items.forEach((item) => {
    let label;

    if (item.items) {
      item.items.forEach((refinement) => {
        label = getLabel(refinement.label, item);
        itemsToRender.push({ text: `${label}`, value: refinement.value });
      });
    } else if (Array.isArray(item.currentRefinement)) {
      item.currentRefinement.forEach((refinement) => {
        label = getLabel(refinement.label, item);
        itemsToRender.push({ text: label, value: item.value });
      });
    } else {
      label = getLabel(item.label, item);
      itemsToRender.push({ text: label, value: item.value });
    }
  });

  // Add extra CSS classes based on component state
  let containerClassName = "current-refinements";
  if (itemsToRender.length === 0) {
    containerClassName += " current-refinements--empty";
  } else if (isSticky) {
    containerClassName += " current-refinements--sticky";
  }

  let listClassName = "current-refinements__list";
  if (itemsToRender.length === 0) {
    listClassName += " current-refinements__list--empty";
  } else if (!isExpanded) {
    listClassName += " current-refinements__list--shrunk";
  }

  return (
    <div className={containerClassName} ref={containerRef}>
      {itemsToRender.length > 0 && (
        <div className="current-refinements__header">Selected Filters</div>
      )}
      <div className="current-refinements__list-container">
        <div className={listClassName} ref={listRef}>
          {itemsToRender.map((item) => {
            return (
              <CurrentRefinementPill
                key={item.text + item.label}
                refine={props.refine}
                text={item.text}
                value={item.value}
              />
            );
          })}
        </div>
        {hiddenRefinements > 0 && !isExpanded && (
          <div className="current-refinements__expand">
            <button
              className="btn btn-text btn-sm"
              onClick={expandList}
              type="button"
            >
              <span className="tw-hidden sm:tw-inline">{`+ ${hiddenRefinements} more`}</span>
              <span className="sm:tw-hidden">{`+ ${hiddenRefinements}`}</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default connectCurrentRefinements(CurrentRefinements);
